import React, { useEffect, useState } from 'react';
import {
  faTimesCircle,
  faPlusCircle,
  faClipboard,
  faEnvelope,
} from '@fortawesome/free-solid-svg-icons';
import {
  Modal,
  Button,
  Form,
  InputGroup,
  Tooltip,
  OverlayTrigger,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { notify } from 'react-notify-toast';
import clientAxios from '../../../../clients/clientAxios';
import './CertificateIssuer.css';

export default function CertificateIssuer({ show, onHide, student }) {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingCertificates, setLoadingCertificates] = useState({});
  const [carreers, setCarreers] = useState([]);
  const [selectedCarreer, setSelectedCarreer] = useState('');
  const [isLoadingSendEmail, setIsLoadingSendEmail] = useState(true);
  const [certificates, setCertificates] = useState(student.certificates || []);

  const getCarreers = async () => {
    try {
      const { data } = await clientAxios('/carreers/');
      setCarreers(data);
    } catch (error) {
      notify.show('Hubo un inconveniente recuperando carreras', 'error');
    }
  };

  useEffect(() => {
    getCarreers();
  }, []);

  const handleCarreerChange = (e) => {
    setSelectedCarreer(e.target.value);
  };

const createCertificate = async (courseName, classroom = null, classroomId = null, carreerId = null, index = null) => {
  const filterName = student.firstname.trim();
  const filterSurname = student.surname.trim();
  const loadingKey = carreerId ? 'career' : index;

  const certificateData = {
    userEmail: student.email,
    userName: filterName,
    userLastName: filterSurname,
    dateTo: new Date(),
    courseName,
    classroom,
    classroomId,
    carreerId,
    qualitySealCert: true,
    studentId: student._id,
  };

  const payload = {
    newCertificate: certificateData,
    studentId: student._id,
    studentCertificates: certificates
  };

  try {
    setLoadingCertificates(prev => ({ ...prev, [loadingKey]: true }));
    const { data } = await clientAxios.post('/certificate/', payload);
    if (data?.success) {
      const newCert = {
        url: data.certificateUrl,
        _id: data.certificateId,
        courseName
      };
      setCertificates(prev => [...prev, newCert]);
      notify.show('Certificado creado!', 'success');
      return;
    }
    if (data?.certificateUrl && data?.certificateId) {
      const existingCert = {
        url: data.certificateUrl,
        _id: data.certificateId,
        courseName
      };
      setCertificates(prev => [...prev, existingCert]);
      notify.show(data.message || 'El certificado ya existe', 'warning');
      return;
    }

  } catch (err) {
    if (err.response?.status === 409 && err.response?.data?.certificateUrl) {
      const existingCert = {
        url: err.response.data.certificateUrl,
        _id: err.response.data.certificateId,
        courseName
      };
      setCertificates(prev => [...prev, existingCert]);
      notify.show(err.response.data.message, 'warning');
    } else if (err.response?.data?.message) {
      notify.show(err.response.data.message, 'error');
    } else {
      notify.show(err.message, 'error');
    }
  } finally {
    setLoadingCertificates(prev => ({ ...prev, [loadingKey]: false }));
  }
};

  const copyToClipboard = (value) => {
    navigator.clipboard.writeText(value)
      .then(() => notify.show('Se ha copiado al portapapeles', 'success'))
      .catch((error) => notify.show(error, 'error'));
  };

  const reSendEmail = async (certificateUrl, courseName) => {
    try {
      setIsLoadingSendEmail(false);
      const studentDataForEmail = {
        userName: student.firstname + ' ' + student.surname,
        courseName,
        userEmail: student.email,
        certificateUrl
      };
      await clientAxios.post('certificate/email', studentDataForEmail);
      notify.show('Se ha enviado el email con éxito', 'success');
    } catch (error) {
      notify.show('Ha ocurrido un error al intentar enviar el email.', 'error');
    } finally {
      setIsLoadingSendEmail(true);
    }
  };

  const getCertificateByCourseName = (name) => {
    return certificates.find((c) => c?.courseName === name);
  };

  return (
    <Modal scrollable size="xl" dialogClassName="modal-90w" show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Crear Certificado</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Carreras */}
        <Form.Group className="mb-4">
          <Form.Label>Seleccione una Carrera:</Form.Label>
          <Form.Control as="select" value={selectedCarreer} onChange={handleCarreerChange}>
            <option value="">-- Seleccionar carrera --</option>
            {carreers?.map((carreer) => (
              <option key={carreer._id} value={carreer._id}>
                {carreer.name}
              </option>
            ))}
          </Form.Control>
          {selectedCarreer && (
            (() => {
              const selectedCareer = carreers.find(c => c._id === selectedCarreer);
              const cert = getCertificateByCourseName(selectedCareer?.certificateCareerName);
              return cert ? (
                <div className="mt-2">
                  <InputGroup>
                    <Form.Control value={cert.url} readOnly />
                  </InputGroup>
                  <div className="d-flex flex-row-reverse mt-2">
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip><strong>Reenviar email</strong></Tooltip>}
                    >
                      <Button
                        variant="primary"
                        size="sm"
                        disabled={!isLoadingSendEmail}
                        onClick={() => reSendEmail(cert.url, cert.courseName)}
                      >
                        <FontAwesomeIcon icon={faEnvelope} bounce />
                      </Button>
                    </OverlayTrigger>
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip><strong>Copiar al portapapeles</strong></Tooltip>}
                    >
                      <Button
                        variant="primary"
                        size="sm"
                        className="mx-1"
                        onClick={() => copyToClipboard(cert.url)}
                      >
                        <FontAwesomeIcon icon={faClipboard} />
                      </Button>
                    </OverlayTrigger>
                  </div>
                </div>
              ) : (
                loadingCertificates.career ? (
                  <div className="skeleton mt-2" style={{ height: '38px', width: '200px' }}></div>
                ) : (
                  <Button
                    className="mt-2 d-block"
                    variant="success"
                    onClick={() =>
                      createCertificate(
                        selectedCareer?.certificateCareerName,
                        'Carrera FullStack',
                        null,
                        selectedCareer?._id
                      )
                    }
                  >
                    <FontAwesomeIcon icon={faPlusCircle} /> Agregar certificado de carrera
                  </Button>
                )
              );
            })()
          )}
        </Form.Group>

        {/* Comisiones */}
        <p className="mt-3">Seleccione una comisión:</p>
        {student.classroom.map((classroom, index) => {
          const cert = getCertificateByCourseName(student.course[index]);
          return (
            <div key={index} className="mb-3">
              {cert ? (
                <>
                  <InputGroup>
                    <h6 className="mr-2 mt-2">Comisión {classroom} - </h6>
                    <Form.Control value={cert.url} readOnly />
                  </InputGroup>
                  <div className="d-flex flex-row-reverse mt-2">
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip><strong>Reenviar email</strong></Tooltip>}
                    >
                      <Button
                        variant="primary"
                        size="sm"
                        disabled={!isLoadingSendEmail}
                        onClick={() => reSendEmail(cert.url, cert.courseName)}
                      >
                        <FontAwesomeIcon icon={faEnvelope} bounce />
                      </Button>
                    </OverlayTrigger>
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip><strong>Copiar al portapapeles</strong></Tooltip>}
                    >
                      <Button
                        variant="primary"
                        size="sm"
                        className="mx-1"
                        onClick={() => copyToClipboard(cert.url)}
                      >
                        <FontAwesomeIcon icon={faClipboard} />
                      </Button>
                    </OverlayTrigger>
                  </div>
                </>
              ) : loadingCertificates[index] ? (
                <div className="skeleton" style={{ height: '38px', width: '100%' }}></div>
              ) : (
                <Button
                  className="d-block w-100"
                  variant="primary"
                  onClick={() => createCertificate(
                    student.course[index],
                    classroom,
                    student.classroomData?.find(e => e?.classroom === classroom)?._id,
                    null,
                    index
                  )}
                >
                  <FontAwesomeIcon icon={faPlusCircle} /> Agregar certificado - Comisión {classroom}
                </Button>
              )}
            </div>
          );
        })}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={onHide}>
          <FontAwesomeIcon icon={faTimesCircle} /> Cerrar
        </Button>
      </Modal.Footer>
    </Modal>
  );
}